import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import {
  ChevronLeft, ChevronRight, BookOpen, GraduationCap,
  Code, Server, Radio, Zap, Cog, Building,
  Cpu, Database, Gauge, Factory, FlaskConical, Beaker
} from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const VTU_LOGO = "https://customer-assets.emergentagent.com/job_prism-labs-2/artifacts/s5v94ogg_image.png";
const BMSCE_LOGO = "https://customer-assets.emergentagent.com/job_prism-labs-2/artifacts/czx8x0ru_image.png";

const UNIVERSITIES = {
  vtu: { name: "Visvesvaraya Technological University", short_name: "VTU", location: "Belagavi, Karnataka", image: VTU_LOGO },
  bmsce: { name: "BMS College of Engineering (Autonomous)", short_name: "BMSCE", location: "Bangalore, Karnataka", image: BMSCE_LOGO },
};

// Icon mapping for branches
const branchIcons = {
  code: Code,
  server: Server,
  radio: Radio,
  zap: Zap,
  cog: Cog,
  building: Building,
  cpu: Cpu,
  database: Database,
  gauge: Gauge,
  factory: Factory,
  flask: FlaskConical,
  beaker: Beaker,
};

export default function BranchPage() {
  const { uniId, branchCode } = useParams();
  const [branch, setBranch] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSem, setSelectedSem] = useState(null);
  const [loading, setLoading] = useState(false);

  const university = UNIVERSITIES[uniId];

  useEffect(() => {
    axios.get(`${API}/branches/${branchCode}`).then((r) => setBranch(r.data)).catch(() => {});
  }, [branchCode]);

  useEffect(() => {
    if (selectedSem) {
      setLoading(true);
      axios
        .get(`${API}/branch/${branchCode}/semester/${selectedSem}/subjects?university_id=${uniId}`)
        .then((r) => setSubjects(r.data.subjects || []))
        .catch(() => setSubjects([]))
        .finally(() => setLoading(false));
    }
  }, [branchCode, selectedSem, uniId]);

  const goBack = () => {
    if (selectedSem) {
      setSelectedSem(null);
      setSubjects([]);
    }
  };

  const getIcon = (iconName) => {
    return branchIcons[iconName] || Code;
  };

  if (!branch || !university) {
    return <div className="min-h-screen pt-24 text-center text-zinc-500">Loading...</div>;
  }

  const IconComponent = getIcon(branch.icon);

  return (
    <div data-testid="branch-page" className="min-h-screen pt-24 pb-12">
      <div className="absolute top-0 left-0 w-full h-[400px] bg-gradient-to-b from-violet-600/5 to-transparent pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          {/* Breadcrumb */}
          <motion.div variants={fadeInUp} className="mb-6">
            {selectedSem ? (
              <button data-testid="branch-back-btn" onClick={goBack}
                className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors">
                <ChevronLeft className="w-4 h-4" />Back to Semesters
              </button>
            ) : (
              <Link to={`/university/${uniId}`}
                className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors">
                <ChevronLeft className="w-4 h-4" />Back to {university.short_name} Branches
              </Link>
            )}
          </motion.div>

          {/* Header */}
          <motion.div variants={fadeInUp} className="flex items-center gap-4 mb-10">
            <div className="shrink-0 w-14 h-14 rounded-xl bg-violet-600/10 border border-white/10 flex items-center justify-center">
              <IconComponent className="w-7 h-7 text-violet-400" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight text-white">{branch.code}</h1>
              <p className="text-sm text-zinc-500 mt-0.5">{branch.name}</p>
              <p className="text-xs text-zinc-600">{university.short_name}</p>
            </div>
          </motion.div>

          <AnimatePresence mode="wait">
            {/* Step 1: Semester Selection */}
            {!selectedSem && (
              <motion.div key="semesters" initial="hidden" animate="visible" exit={{ opacity: 0, y: -10 }} variants={stagger}>
                <motion.div variants={fadeInUp} className="mb-8">
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-white mb-1">Select Semester</h2>
                  <p className="text-sm text-zinc-500">Choose your semester to browse subjects</p>
                </motion.div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[1, 3, 4, 5, 6, 7, 8].map((sem) => (
                    <motion.button key={sem} variants={fadeInUp}
                      data-testid={`semester-btn-${sem}`}
                      onClick={() => setSelectedSem(sem)}
                      className="group relative rounded-2xl bg-white/[0.03] border border-white/10 p-6 hover:border-violet-500/30 hover:-translate-y-1 hover:shadow-[0_0_30px_-10px_rgba(139,92,246,0.2)] text-left"
                      style={{ transitionProperty: "border-color, transform, box-shadow", transitionDuration: "300ms" }}>
                      <div className="font-heading text-3xl font-bold text-violet-400 mb-2">
                        {sem === 1 ? '1Y' : sem}
                      </div>
                      <div className="text-sm text-zinc-400 group-hover:text-white transition-colors">
                        {sem === 1 ? "1st Year" : sem === 3 ? "3rd Semester" : `${sem}th Semester`}
                      </div>
                      <ChevronRight className="absolute top-1/2 right-4 -translate-y-1/2 w-5 h-5 text-zinc-700 group-hover:text-violet-400 transition-colors" />
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 2: Subject Selection */}
            {selectedSem && (
              <motion.div key="subjects" initial="hidden" animate="visible" exit={{ opacity: 0, y: -10 }} variants={stagger}>
                <motion.div variants={fadeInUp} className="mb-8">
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-white mb-1">
                    {selectedSem === 1 ? '1st Year' : `Semester ${selectedSem}`} Subjects
                  </h2>
                  <p className="text-sm text-zinc-500">{branch.code} &middot; {subjects.length} subject{subjects.length !== 1 ? "s" : ""}</p>
                </motion.div>

                {loading ? (
                  <div className="text-center py-12 text-zinc-500">Loading subjects...</div>
                ) : subjects.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {subjects.map((subject) => (
                      <motion.div key={subject.id} variants={fadeInUp}>
                        <Link
                          to={`/subject/${subject.id}`}
                          data-testid={`subject-card-${subject.id}`}
                          className="group block rounded-2xl bg-white/[0.03] border border-white/10 p-5 hover:border-violet-500/30 hover:-translate-y-1 hover:shadow-[0_0_30px_-10px_rgba(139,92,246,0.2)]"
                          style={{ transitionProperty: "border-color, transform, box-shadow", transitionDuration: "300ms" }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="shrink-0 rounded-lg bg-violet-600/10 p-2.5">
                              <BookOpen className="w-5 h-5 text-violet-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-heading text-sm font-semibold text-white mb-0.5 group-hover:text-violet-200 transition-colors line-clamp-2">
                                {subject.name}
                              </h3>
                              <p className="text-xs text-zinc-600">{subject.code} &middot; {subject.credits || 4} Credits</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-violet-400 group-hover:text-violet-300 transition-colors mt-3">
                            View Resources <ChevronRight className="w-3 h-3" />
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="inline-flex items-center justify-center rounded-2xl bg-zinc-800/50 p-4 mb-4">
                      <BookOpen className="w-8 h-8 text-zinc-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">No Subjects Yet</h3>
                    <p className="text-sm text-zinc-500 max-w-md mx-auto">
                      Subjects for {branch.code} {selectedSem === 1 ? '1st Year' : `Semester ${selectedSem}`} will appear here once added by the admin.
                    </p>
                    <Link
                      to="/resources"
                      className="inline-flex items-center gap-2 mt-6 text-sm text-violet-400 hover:text-violet-300"
                    >
                      Browse All Resources <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
